import styled from '@emotion/styled/macro';
import { SvgIcon, SvgNames, tokens } from '@sunrun/experience-ui-components';
import { ReactNode } from 'react';
import { usePullToRefresh } from 'hooks/usePullToRefresh';

type PullToRefreshProps = {
  onRefresh: () => void | Promise<unknown>;
  isRefreshing?: boolean;
  enabled?: boolean;
  children: ReactNode;
};

const PullToRefresh = ({
  onRefresh,
  isRefreshing = false,
  enabled = true,
  children,
}: PullToRefreshProps) => {
  const {
    setContainerElement,
    setContentElement,
    setIndicatorElement,
    setSpinnerElement,
  } = usePullToRefresh({ onRefresh, isRefreshing, enabled });

  return (
    <Container ref={setContainerElement}>
      <Indicator ref={setIndicatorElement} aria-hidden="true">
        <Spinner ref={setSpinnerElement}>
          <SvgIcon
            name={SvgNames.Loading}
            width={24}
            height={24}
            color={tokens.BRAND_HERO_BLUE}
          />
        </Spinner>
      </Indicator>
      <Content ref={setContentElement}>{children}</Content>
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
`;

/**
 * Fixed rather than absolute so the spinner centers on the viewport instead of
 * the page content, which is much wider than the screen on the pipeline board.
 */
const Indicator = styled.div`
  position: fixed;
  left: 0;
  width: 100vw;
  height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  pointer-events: none;
  z-index: 1;
`;

const Spinner = styled.span`
  display: flex;
  opacity: 0;

  &[data-spinning='true'] {
    animation: pull-to-refresh-spin 0.8s linear infinite;
  }

  @keyframes pull-to-refresh-spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  width: 100%;
  min-height: 0;
`;

export { PullToRefresh };
